/**
 * assistant_context.v1 — the client half of the page-context contract.
 *
 * A page publishes a SEMANTIC REFERENCE to what the user is looking at: which
 * property, which version, which period, which row is selected. Never account
 * values, report totals, vendor transactions, or API responses — the server
 * fetches its own facts, and anything sent from here is a hint it must
 * independently authorize.
 */

export const ASSISTANT_CONTEXT_SCHEMA = 'assistant_context.v1';

export type AssistantPageKey = 'forecast_v3' | 'journal_detail';

export type AssistantSelectionKind =
  | 'forecast_account'
  | 'forecast_department'
  | 'forecast_month'
  | 'journal_day';

export interface AssistantSelection {
  kind: AssistantSelectionKind;
  /** Row-shaped selections. Date-shaped ones (journal_day) send `date` instead. */
  id?: number | null;
  /** ISO YYYY-MM-DD. A business date IS the identity of a journal day. */
  date?: string | null;
  /** Display only — the server resolves its own label from the canonical row. */
  label?: string | null;
}

export interface AssistantDataRef {
  kind: 'forecast_version' | 'budget_version';
  id: number;
}

export interface AssistantContextEnvelope {
  schema: typeof ASSISTANT_CONTEXT_SCHEMA;
  pageKey: AssistantPageKey;
  /** When the UI took this snapshot. NOT a financial as-of watermark. */
  capturedAt: string;
  scope: { orgId: number; propertyIds: number[] };
  view: {
    route?: string | null;
    mode?: string | null;
    versionId?: number | null;
    year?: number | null;
    activeMonth?: number | null;
    /** Date-windowed pages (the Income Journal) describe themselves with a range. */
    startDate?: string | null;
    endDate?: string | null;
  };
  filters: Record<string, string | number>;
  selection: AssistantSelection | null;
  dataRefs: AssistantDataRef[];
  /** Informational. The server replaces this with its own registry's answer. */
  capabilities: string[];
}

/** What a page adapter publishes; the provider normalizes it into an envelope. */
export interface AssistantPageState {
  pageKey: AssistantPageKey;
  orgId: number | null;
  propertyIds: number[];
  view: AssistantContextEnvelope['view'];
  filters?: Record<string, string | number | null | undefined>;
  selection?: AssistantSelection | null;
  dataRefs?: AssistantDataRef[];
  capabilities?: string[];
  /** Human label parts for the context chip, most general first. */
  labelParts?: (string | null | undefined)[];
}

export interface AssistantContextSnapshot {
  envelope: AssistantContextEnvelope;
  hash: string;
  label: string;
}
